<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('general.clients') }}
        </oxd-text>
      </div>

      <table-header :total="total" :loading="isLoading"></table-header>

      <div class="orangehrm-container">
        <oxd-card-table
          v-model:order="sortDefinition"
          :loading="isLoading"
          :headers="headers"
          :items="items?.data"
          :selectable="false"
          :clickable="false"
          row-decorator="oxd-table-decorator-card"
          ref="tableRef"
        >
        </oxd-card-table>
      </div>

      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {computed} from 'vue';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog.vue';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import useSort from '@ohrm/core/util/composable/useSort';

const defaultSortOrder = {'customer.name': 'ASC'};

export default {
  components: {'delete-confirmation': DeleteConfirmationDialog},

  props: {
    unselectableIds: {type: Array, default: () => []},
  },

  setup() {
    const customerNormalizer = (data) =>
      data.map((item) => ({
        id: item.id,
        name: item.name,
        phone: item.phone,
        email: item.email,
      }));

    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });

    const serializedFilters = computed(() => ({
      sortField: sortField.value,
      sortOrder: sortOrder.value,
    }));

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/time/customers',
    );
    const http1 = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/deductions',
    );

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: customerNormalizer,
    });

    onSort(execQuery);

    return {
      http,
      http1,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      sortDefinition,
    };
  },

  data() {
    return {
      currentMonthNumber: new Date().getMonth() + 1, // 1..12

      deductions: {taxYear: ''},
      clients: null, // { data: [...] }

      headers: [
        {
          name: 'name',
          slot: 'title',
          title: this.$t('general.name'),
          sortField: 'customer.name',
          style: {flex: 2},
          cellRenderer: (index, item, header, row) => ({
            props: {
              innerHTML: `
                <div class="customer-name">
                  <p class="customer-name-text">${row.name}</p>
                </div>
              `,
            },
          }),
        },
        {
          name: 'phone',
          title: this.$t('general.phone'),
          style: {flex: 1},
        },
        {
          name: 'email',
          title: this.$t('general.email'),
          style: {flex: 2},
        },
        {
          name: 'months',
          title: this.$t('general.months'),
          style: {flex: 8},
          cellRenderer: (index, item, header, row) => {
            const months = [
              {number: 1, name: 'Jan'},
              {number: 2, name: 'Feb'},
              {number: 3, name: 'Mar'},
              {number: 4, name: 'Apr'},
              {number: 5, name: 'May'},
              {number: 6, name: 'Jun'},
              {number: 7, name: 'Jul'},
              {number: 8, name: 'Aug'},
              {number: 9, name: 'Sep'},
              {number: 10, name: 'Oct'},
              {number: 11, name: 'Nov'},
              {number: 12, name: 'Dec'},
            ];

            const buttons = months
              .filter((m) => m.number <= this.currentMonthNumber)
              .map((m) => {
                const elId = `bal-${row.id}-${m.number}`;

                // paint after the element exists; no caching/state
                this.paintMonthBalance(row.id, m.number, elId);

                const fnName = `handleMonthClick_${row.id}_${m.number}`;
                window[fnName] = () => {
                  const handler = this[`onClick${m.name}`];
                  if (typeof handler === 'function') handler(row);
                };

                return `
                  <button
                    id="${elId}"
                    style="
                      min-width: 52px; height: 30px; padding: 4px 8px;
                      border: 1px solid #ddd; border-radius: 5px; background: white;
                      cursor: pointer; color: #888; font-size: 12px; font-weight: 600;
                      margin: 2px; transition: all 0.2s ease;
                    "
                    onmouseover="this.style.background='#f8f9fa'; this.style.transform='translateY(-1px)'"
                    onmouseout="this.style.background='white'; this.style.transform='translateY(0)'"
                    onclick="window.${fnName}()"
                  >
                    ${m.name}
                  </button>
                `;
              })
              .join('');

            return {
              props: {
                innerHTML: `
                  <div style="display:flex;flex-wrap:wrap;gap:3px;padding:2px;">
                    ${buttons}
                  </div>
                `,
              },
            };
          },
        },
      ],

      checkedItems: [],
    };
  },

  created() {
    this.getTaxYear();
    this.getCustomers();
  },

  methods: {
    async paintMonthBalance(custId, month, elId) {
      this.$nextTick(async () => {
        const el = document.getElementById(elId);
        if (!el || el.dataset.done === '1') return;
        el.dataset.done = '1';

        try {
          if (!this.clients?.data) return;

          const cust = this.clients.data.find(
            (c) => String(c.id) === String(custId),
          );
          if (!cust) return;

          const [fromDate] = this.getFromTo(month);
          if (!fromDate) return;

          const invoiceNumber = `${cust.name.substring(
            0,
            3,
          )}-${fromDate.substring(0, 7)}`;

          // fetch amounts
          const paid = await this.getTotalPaid(custId, invoiceNumber);
          const total = await this.getTotalCost(custId, month);

          const totalNum = Number(total);
          const balance = totalNum - Number(paid);

          // total==0 => grey; else balance==0 => green; else red
          let color = '#888';
          if (Number.isFinite(totalNum) && totalNum > 0) {
            color = balance === 0 ? 'green' : 'red';
          }
          el.style.color = color;

          // only show (balance) when total > 0 and balance ≠ 0
          if (Number.isFinite(totalNum) && totalNum > 0 && balance !== 0) {
            el.textContent = `${el.textContent}`;
          }
        } catch (e) {
          console.error('paintMonthBalance error', e);
          // leave grey on error
        }
      });
    },
    // ---- Data fetchers ----
    async getCustomers() {
      const str = '/api/v2/time/customers';
      const svc = new APIService(window.appGlobal.baseUrl, str);
      const response = await svc.getAll();
      this.clients = response.data; // expect { data: [...] }
      return this.clients;
    },

    async getTaxYear() {
      this.isLoading = true;
      try {
        const response = await this.http1.http.get('/api/v2/pim/deductions');
        const {data} = response.data;
        this.deductions.taxYear = data.taxyear;
      } finally {
        this.isLoading = false;
      }
    },

    async getTotalCost(custId, month) {
      try {
        const path = `/api/v2/pim/invoices/cust/${encodeURIComponent(
          custId,
        )}/month/${encodeURIComponent(month)}`;
        const svc = new APIService(window.appGlobal.baseUrl, path);
        const res = await svc.getAll();
        const body = res?.data ?? {};
        const items = Array.isArray(body.data) ? body.data : [];
        const fromMeta = Number(body?.meta?.totalCost);

        if (items.length > 0 && Number.isFinite(fromMeta)) return fromMeta;
        if (items.length === 0) return 0;

        // fallback sum if meta missing
        return items.reduce(
          (sum, it) =>
            sum + Number(it?.totalCost ?? it?.total ?? it?.amount ?? 0),
          0,
        );
      } catch (err) {
        console.error('getTotalCost error:', err);
        return 0;
      }
    },

    async getTotalPaid(custId, invoiceNumber) {
      try {
        const path = `/api/v2/pim/invoicepayments/${encodeURIComponent(
          custId,
        )}/${encodeURIComponent(invoiceNumber)}`;
        const svc = new APIService(window.appGlobal.baseUrl, path);
        const res = await svc.getAll();
        const body = res?.data ?? {};
        const list = Array.isArray(body.data) ? body.data : [];
        const paid = Number(body?.meta?.totalAmount ?? 0);
        if (list.length === 0) return 0;
        return Number.isFinite(paid) ? paid : 0;
      } catch (err) {
        console.error('getTotalPaid error:', err);
        return 0;
      }
    },

    // Safer month ranges using real calendar month length
    getFromTo(monthId) {
      const year = Number(this.deductions?.taxYear) || new Date().getFullYear();
      const m = Number(monthId); // 1..12
      if (!Number.isFinite(m) || m < 1 || m > 12) return ['', ''];

      const fromDate = new Date(year, m - 1, 1);
      const toDate = new Date(year, m, 0); // last day of month

      const pad = (n) => String(n).padStart(2, '0');
      const from = `${fromDate.getFullYear()}-${pad(
        fromDate.getMonth() + 1,
      )}-01`;
      const to = `${toDate.getFullYear()}-${pad(toDate.getMonth() + 1)}-${pad(
        toDate.getDate(),
      )}`;

      return [from, to];
    },

    // ---- Navigation ----
    onClickAdd() {
      navigate('/time/addCustomer');
    },
    onClickJan(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '1',
      });
    },
    onClickFeb(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '2',
      });
    },
    onClickMar(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '3',
      });
    },
    onClickApr(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '4',
      });
    },
    onClickMay(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '5',
      });
    },
    onClickJun(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '6',
      });
    },
    onClickJul(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '7',
      });
    },
    onClickAug(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '8',
      });
    },
    onClickSep(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '9',
      });
    },
    onClickOct(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '10',
      });
    },
    onClickNov(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '11',
      });
    },
    onClickDec(item) {
      navigate('/pim/runInvoice/cust/{cust}/month/{month}', {
        cust: item.id,
        month: '12',
      });
    },

    // ---- Delete ----
    onClickDeleteSelected() {
      const ids = this.checkedItems.map((index) => this.items?.data[index].id);
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') this.deleteItems(ids);
      });
    },
    onClickDelete(item) {
      const isSelectable = this.unselectableIds.findIndex(
        (id) => id == item.id,
      );
      if (isSelectable > -1) {
        return this.$toast.error({
          title: this.$t('general.error'),
          message: this.$t(
            'time.not_allowed_to_delete_customer_who_have_time_logged_against',
          ),
        });
      }
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') this.deleteItems([item.id]);
      });
    },
    deleteItems(items) {
      if (items instanceof Array) {
        this.isLoading = true;
        this.http
          .deleteAll({ids: items})
          .then(() => this.$toast.deleteSuccess())
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
  },
};
</script>
